<template>
  <div class="flex min-w-0 w-full items-center gap-2">
    <div :class="[containerClass, statusClasses, { 'border-none': group, 'flex-col': stacked }]">
      <div class="min-w-0 flex-1">
        <UInputDate
          v-model="startValue"
          :locale="$i18n.locale.value"
          :max-value="limit && finishCalendarDate ? finishCalendarDate : undefined"
          :disabled="disabled"
          size="sm"
          color="neutral"
          variant="none"
          class="w-full min-w-0"
          :ui="{ base: 'min-h-8' }"
          :aria-label="$t('general.from')"
        >
          <template v-if="startValue" #trailing>
            <UButton
              color="error"
              variant="link"
              icon="i-lucide-x"
              size="xs"
              :disabled="disabled"
              :aria-label="$t('general.clear')"
              @click.stop="clearStart"
            />
          </template>
        </UInputDate>
      </div>
      <div
        class="flex shrink-0 items-center self-center text-gray-400 transition-transform dark:text-gray-500"
        :class="{ 'rotate-90': stacked }"
      >
        <o-icon icon="chevronRight" :size="10" />
      </div>
      <div class="min-w-0 flex-1">
        <UInputDate
          v-model="finishValue"
          :locale="$i18n.locale.value"
          :min-value="limit && startCalendarDate ? startCalendarDate : undefined"
          :disabled="disabled"
          size="sm"
          color="neutral"
          variant="none"
          class="w-full min-w-0"
          :ui="{ base: 'min-h-8' }"
          :aria-label="$t('general.to')"
        >
          <template v-if="finishValue" #trailing>
            <UButton
              color="error"
              variant="link"
              icon="i-lucide-x"
              size="xs"
              :disabled="disabled"
              :aria-label="$t('general.clear')"
              @click.stop="clearFinish"
            />
          </template>
        </UInputDate>
      </div>
    </div>

    <o-dropdown v-if="options.length">
      <o-button icon="preset" flat size="sm" />

      <template #content>
        <o-dropdown-item
          v-for="(preset, index) in options"
          :key="index"
          @click="() => set(preset)"
        >
          {{ $t(preset.name) }}
        </o-dropdown-item>
      </template>
    </o-dropdown>
  </div>
</template>

<script setup lang="ts">
import { CalendarDate, type DateValue } from '@internationalized/date'
import { augmenters } from '@/composables/useFieldAugmenters'
import ODropdown from '@/components/elements/dropdown/Dropdown.vue'
import ODropdownItem from '@/components/elements/dropdown/DropdownItem.vue'

const { $auth, $i18n } = useNuxtApp()

interface Props {
  modelValue?: Record<string, any> | null
  format?: string | null
  augmenter?: Record<string, any>
  group?: boolean
  stacked?: boolean
  icon?: boolean
  limit?: boolean
  options?: any[]
  containerClass?: string
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  size?: string | null
  status?: boolean | string | null
  disabled?: boolean
  baseClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  format: null,
  augmenter: () => augmenters.timestamp,
  group: false,
  stacked: false,
  icon: true,
  limit: true,
  options: () => [],
  containerClass:
    'flex min-w-0 flex-1 items-stretch gap-2 overflow-hidden rounded-lg border border-gray-200 bg-white px-2 py-1.5 dark:border-gray-700 dark:bg-gray-900',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-400 bg-yellow-100 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-300 bg-green-100 dark:bg-green-400/20',
  size: null,
  status: null,
  disabled: false,
  baseClass: undefined,
  disabledClass: undefined,
  defaultSizeClass: undefined,
  largeSizeClass: undefined,
  smallSizeClass: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change', value: any): void
  (e: 'changeStart', value: any): void
  (e: 'changeFinish', value: any): void
}>()

const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

function calendarDateToDate(cd: DateValue): Date {
  return new Date(cd.year, cd.month - 1, cd.day)
}

function rawToCalendarDate(raw: any): CalendarDate | undefined {
  if (raw == null) return undefined
  const date = props.augmenter?.input ? props.augmenter.input(raw) : raw
  if (date instanceof Date) return dateToCalendarDate(date)
  return undefined
}

function calendarDateToRaw(cd: DateValue): any {
  const date = calendarDateToDate(cd)
  return props.augmenter?.output ? props.augmenter.output(date) : date
}

const startCalendarDate = computed(() => rawToCalendarDate((props.modelValue as any)?.start))
const finishCalendarDate = computed(() => rawToCalendarDate((props.modelValue as any)?.finish))

const startValue = computed({
  get(): DateValue | undefined {
    return startCalendarDate.value
  },
  set(value: DateValue | undefined) {
    const raw = value ? calendarDateToRaw(value) : null
    const params = { old: (props.modelValue as any)?.start, new: raw }
    const updated = { ...(props.modelValue || {}), start: raw }
    emit('update:modelValue', updated)
    emit('change', params)
    emit('changeStart', params)
  },
})

const finishValue = computed({
  get(): DateValue | undefined {
    return finishCalendarDate.value
  },
  set(value: DateValue | undefined) {
    const raw = value ? calendarDateToRaw(value) : null
    const params = { old: (props.modelValue as any)?.finish, new: raw }
    const updated = { ...(props.modelValue || {}), finish: raw }
    emit('update:modelValue', updated)
    emit('change', params)
    emit('changeFinish', params)
  },
})

function clearStart() {
  startValue.value = undefined as any
}

function clearFinish() {
  finishValue.value = undefined as any
}

function set(preset: any) {
  if (!preset) return

  const startDate = preset.value.start instanceof Date ? preset.value.start : new Date(preset.value.start * 1000)
  const finishDate = preset.value.finish instanceof Date ? preset.value.finish : new Date(preset.value.finish * 1000)

  const startRaw = props.augmenter?.output ? props.augmenter.output(startDate) : startDate
  const finishRaw = props.augmenter?.output ? props.augmenter.output(finishDate) : finishDate

  const startParams = { old: (props.modelValue as any)?.start, new: startRaw }
  const finishParams = { old: (props.modelValue as any)?.finish, new: finishRaw }

  emit('update:modelValue', { ...(props.modelValue || {}), start: startRaw, finish: finishRaw })
  emit('change', startParams)
  emit('changeStart', startParams)
  emit('change', finishParams)
  emit('changeFinish', finishParams)
}
</script>
